// Shared numeric + time formatters for the analytics chart kit. Pure, tree-shakeable, and
// tested independently so the tabular-numeral + "updated Xm ago" contracts live in ONE place.

/**
 * A compact human number: `50800 → "50.8k"`, `4_500_000 → "4.5m"`, `320 → "320"`. One decimal, trailing `.0`
 * trimmed. Used for the KPI tiles / bar values; the tile itself owns the null → `—` decision (never here).
 */
export function compactNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const fmt = (value: number, suffix: string) => {
    const one = Math.round(value * 10) / 10;
    return (Number.isInteger(one) ? one.toString() : one.toFixed(1)) + suffix;
  };
  if (abs >= 1_000_000_000) return fmt(n / 1_000_000_000, "b");
  if (abs >= 1_000_000) return fmt(n / 1_000_000, "m");
  if (abs >= 1_000) return fmt(n / 1_000, "k");
  return n.toString();
}

/**
 * A compact relative-time stamp for the `as_of` staleness marker (the "updated Xm ago" marker):
 * `just now` / `Xm ago` / `Xh ago` / `Xd ago`. Returns null for a null/invalid input so the caller renders no
 * marker. `now` is injectable for deterministic tests.
 */
export function formatRelativeTime(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((now - then) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}
